import {
  type BlockWireframe,
  selectBlocksByNode,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import { useRef } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { AI_ATTEMPTS, VITE_AI_HOST } from '@/constants/env'
import type { AppState } from '@/types'
import { parseBlocks, postEventStreamV2 } from '@/utils/streamsV2'
import {
  addFilledBlock,
  addFooter,
  addHeader,
  clearAiNotifications,
  notifyAiGeneratingFinished,
  notifyAiInProgress,
  saveGeneratedBlocks,
  updateBlockAndSave,
} from './actionCreators'
import {
  parseBlockWireframeAndText,
  prepareContext,
  removeQuotes,
} from './utils'

const useGenerateBlocks = (nodeId: string) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const store = useStore()
  const blockIdsRef = useRef<string[]>([])
  const insertIndexRef = useRef<number | null>(null)

  const nodeBlocks = useSelector(selectBlocksByNode(nodeId))
  const blockTitles = useSelector(
    migrationProjectSelector.getProjectBlocksOrSymbolTitlesByIds(nodeBlocks)
  )

  const process = (prompt: string) => {
    blockIdsRef.current = []
    insertIndexRef.current = null

    if (!prompt) {
      return
    }

    let currentBlockId: string | undefined

    // const footerBlockId = blocks.byNodeId[nodeId]?.find((blockId) => {
    //   const b = blocks[blockId] as MonolithicBlock
    //   return b.title === 'Footer'
    // })
    //
    // insertIndexRef.current =
    //   footerBlockId !== undefined
    //     ? blocks.byNodeId[nodeId].indexOf(footerBlockId)
    //     : -1

    // @todo implement logic
    insertIndexRef.current = 1

    const [stream] = postEventStreamV2(
      `${VITE_AI_HOST}/v2/generate-blocks`,
      {
        context: prepareContext(store.getState() as AppState, nodeId),
        theme: prompt,
      },
      { attempts: AI_ATTEMPTS }
    )

    onStart()

    const parsedStream = parseBlocks(stream)

    parsedStream.on('block', ({ value, index, terminal }) => {
      value = removeQuotes(value).trim()

      const parsedBlock = parseBlockWireframeAndText(value)

      if (!parsedBlock) {
        return
      }

      const { wireframe, text } = parsedBlock

      const insertIndex =
        insertIndexRef.current === -1
          ? undefined
          : insertIndexRef.current! + index

      if (!currentBlockId) {
        currentBlockId = dispatch(
          addFilledBlock({
            nodeId,
            title: text,
            wireframes: [wireframe as BlockWireframe],
            index: insertIndex,
          })
        ) as unknown as string
        blockIdsRef.current.push(currentBlockId)
      } else {
        dispatch(updateBlockAndSave(currentBlockId, text))
      }

      if (terminal) {
        currentBlockId = undefined
      }
    })

    stream.on('error', onFinish)
    stream.on('success', onFinish)
  }

  const onStart = () => {
    dispatch(artBoardActions.setAiGeneratingStatus({ status: true }))
    dispatch(notifyAiInProgress())

    if (!blockTitles.includes('Header')) {
      const headerId = dispatch(addHeader(nodeId)) as unknown as string
      blockIdsRef.current.push(headerId)
    }
  }

  const onFinish = () => {
    if (!blockTitles.includes('Footer')) {
      const footerId = dispatch(addFooter(nodeId)) as unknown as string
      blockIdsRef.current.push(footerId)
    }

    dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
    dispatch(clearAiNotifications())
    dispatch(notifyAiGeneratingFinished())
    setTimeout(() => {
      dispatch(clearAiNotifications())
    }, 8000)

    dispatch(
      saveGeneratedBlocks(
        blockIdsRef.current,
        nodeId,
        insertIndexRef.current === -1
          ? undefined
          : (insertIndexRef.current ?? undefined)
      )
    )
  }

  return process
}

export default useGenerateBlocks
